import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Skeleton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Refresh,
  Add,
  PlayArrow,
  CheckCircle,
  Verified,
  Close,
  Warning,
  Security,
  Assignment,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getJwtPayload(): any {
  try {
    const token = localStorage.getItem('token');
    return token ? JSON.parse(atob(token.split('.')[1])) : {};
  } catch {
    return {};
  }
}

function getUserIdFromToken(): string {
  const payload = getJwtPayload();
  return payload.sub || '';
}

interface ControlPoint {
  id: string;
  ccpNumber: string;
  name: string;
  description?: string;
  controlPointType: string;
  hazardType: string;
  hazardDescription: string;
  controlMeasure: string;
  criticalLimit: string;
  monitoringProcedure: string;
  monitoringFrequency: string;
  correctiveActionPlan: string;
  verificationProcedure: string;
  verificationFrequency: string;
  isActive: boolean;
  createdAt: string;
}

interface CorrectiveAction {
  id: string;
  controlPointId: string;
  description: string;
  rootCause: string;
  immediateAction: string;
  preventiveAction?: string;
  severity: string;
  status: string;
  assignedTo: string;
  dueDate?: string;
  completedAt?: string;
  verifiedAt?: string;
  affectedLots?: string[];
  productDisposition?: string;
  createdAt: string;
}

interface MonitoringRecord {
  id: string;
  controlPointId: string;
  measuredValue: string | number;
  unit: string;
  recordedBy: string;
  notes?: string;
  createdAt: string;
  status?: string;
}

interface HaccpSummary {
  activeCCPs: number;
  totalDeviations: number;
  openCorrectiveActions: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const cpTypeColor: Record<string, 'error' | 'warning' | 'info'> = {
  ccp: 'error',
  oprp: 'warning',
  prp: 'info',
};

const hazardColor: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  biological: 'error',
  chemical: 'warning',
  physical: 'info',
  allergen: 'success',
  radiological: 'default',
};

const caStatusColor: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  open: 'error',
  in_progress: 'warning',
  completed: 'info',
  verified: 'success',
  closed: 'default',
};

export default function HACCP() {
  const [tab, setTab] = useState(0);
  const [ccps, setCcps] = useState<ControlPoint[]>([]);
  const [actions, setActions] = useState<CorrectiveAction[]>([]);
  const [summary, setSummary] = useState<HaccpSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monitoring records state
  const [monitoringRecords, setMonitoringRecords] = useState<MonitoringRecord[]>([]);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [addMonitoringOpen, setAddMonitoringOpen] = useState(false);
  const [monitoringForm, setMonitoringForm] = useState({
    controlPointId: '',
    measuredValue: '',
    unit: '',
    notes: '',
  });
  const [monitoringSubmitting, setMonitoringSubmitting] = useState(false);
  const [monitoringSnackbar, setMonitoringSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Create CCP dialog
  const [createCcpOpen, setCreateCcpOpen] = useState(false);
  const [newCcp, setNewCcp] = useState({
    ccpNumber: '',
    name: '',
    controlPointType: 'ccp',
    hazardType: 'biological',
    hazardDescription: '',
    controlMeasure: '',
    criticalLimit: '',
    monitoringProcedure: '',
    monitoringFrequency: '',
    correctiveActionPlan: '',
    verificationProcedure: '',
    verificationFrequency: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchMonitoringRecords = useCallback(async () => {
    setMonitoringLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/quality/haccp/monitoring-records`, {
        headers: authHeaders(),
      });
      const data = res.data?.data ?? res.data;
      const records = Array.isArray(data) ? data : [];
      setMonitoringRecords(records.map((record: any) => ({
        ...record,
        unit: record.unit ?? record.unitOfMeasure ?? '',
        recordedBy: record.recordedBy ?? record.monitoredBy ?? '',
        status: record.status ?? record.result,
      })));
    } catch {
      // Non-critical — endpoint may not exist yet
      setMonitoringRecords([]);
    } finally {
      setMonitoringLoading(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ccpRes, caRes, sumRes] = await Promise.all([
        axios.get(`${API_BASE}/quality/haccp/control-points`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/quality/haccp/corrective-actions`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/quality/haccp/summary`, { headers: authHeaders() }),
      ]);
      setCcps(Array.isArray(ccpRes.data?.data) ? ccpRes.data.data : []);
      setActions(Array.isArray(caRes.data?.data) ? caRes.data.data : []);
      setSummary(sumRes.data?.data || null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch HACCP data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchMonitoringRecords();
  }, [fetchAll, fetchMonitoringRecords]);

  const handleSubmitMonitoringRecord = async () => {
    setMonitoringSubmitting(true);
    try {
      await axios.post(`${API_BASE}/quality/haccp/monitoring-records`, {
        controlPointId: monitoringForm.controlPointId,
        measuredValue: monitoringForm.measuredValue,
        unitOfMeasure: monitoringForm.unit,
        monitoredBy: getUserIdFromToken(),
        monitoredAt: new Date().toISOString(),
        result: 'within_limits',
        notes: monitoringForm.notes || undefined,
      }, { headers: authHeaders() });
      setAddMonitoringOpen(false);
      setMonitoringForm({ controlPointId: '', measuredValue: '', unit: '', notes: '' });
      setMonitoringSnackbar({ open: true, message: 'Monitoring record added successfully', severity: 'success' });
      fetchMonitoringRecords();
    } catch (err: any) {
      setMonitoringSnackbar({
        open: true,
        message: err.response?.data?.message || err.message || 'Failed to submit monitoring record',
        severity: 'error',
      });
    } finally {
      setMonitoringSubmitting(false);
    }
  };

  const handleCreateCcp = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/quality/haccp/control-points`, newCcp, { headers: authHeaders() });
      setCreateCcpOpen(false);
      setNewCcp({
        ccpNumber: '', name: '', controlPointType: 'ccp', hazardType: 'biological',
        hazardDescription: '', controlMeasure: '', criticalLimit: '',
        monitoringProcedure: '', monitoringFrequency: '', correctiveActionPlan: '',
        verificationProcedure: '', verificationFrequency: '',
      });
      fetchAll();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create control point');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCaAction = async (id: string, action: string) => {
    try {
      const hdrs = { headers: authHeaders() };
      if (action === 'start') {
        await axios.post(`${API_BASE}/quality/haccp/corrective-actions/${id}/start`, {}, hdrs);
      } else if (action === 'complete') {
        await axios.post(`${API_BASE}/quality/haccp/corrective-actions/${id}/complete`, {}, hdrs);
      } else if (action === 'verify') {
        await axios.post(`${API_BASE}/quality/haccp/corrective-actions/${id}/verify`, {
          verifiedBy: getUserIdFromToken() || '00000000-0000-0000-0000-000000000001',
        }, hdrs);
      } else if (action === 'close') {
        await axios.post(`${API_BASE}/quality/haccp/corrective-actions/${id}/close`, {}, hdrs);
      }
      fetchAll();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Action failed');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            HACCP Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hazard Analysis and Critical Control Points -- food safety monitoring, deviations, and corrective actions.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateCcpOpen(true)}>
            New Control Point
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAll} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Active CCPs</Typography>
                  <Typography variant="h5">{summary?.activeCCPs ?? '-'}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="warning" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Deviations</Typography>
                  <Typography variant="h5" color="warning.main">{summary?.totalDeviations ?? '-'}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment color="error" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Open Actions</Typography>
                  <Typography variant="h5" color="error.main">{summary?.openCorrectiveActions ?? '-'}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label={`Control Points (${ccps.length})`} />
        <Tab label={`Corrective Actions (${actions.length})`} />
        <Tab label={`Monitoring Records (${monitoringRecords.length})`} />
      </Tabs>

      {/* Control Points Tab */}
      <TabPanel value={tab} index={0}>
        {loading && ccps.length === 0 ? (
          <Box>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1, borderRadius: 1 }} />)}</Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell>CCP #</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Hazard</TableCell>
                  <TableCell>Critical Limit</TableCell>
                  <TableCell>Monitoring</TableCell>
                  <TableCell>Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ccps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No control points defined. Create one to begin your HACCP plan.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  ccps.map((ccp) => (
                    <TableRow key={ccp.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{ccp.ccpNumber}</Typography>
                      </TableCell>
                      <TableCell>{ccp.name}</TableCell>
                      <TableCell>
                        <Chip label={ccp.controlPointType.toUpperCase()} size="small" color={cpTypeColor[ccp.controlPointType] || 'default'} />
                      </TableCell>
                      <TableCell>
                        <Chip label={ccp.hazardType} size="small" variant="outlined" color={hazardColor[ccp.hazardType] || 'default'} />
                      </TableCell>
                      <TableCell>{ccp.criticalLimit}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ccp.monitoringProcedure} ({ccp.monitoringFrequency})
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={ccp.isActive ? 'Yes' : 'No'} size="small" color={ccp.isActive ? 'success' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Corrective Actions Tab */}
      <TabPanel value={tab} index={1}>
        {loading && actions.length === 0 ? (
          <Box>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1, borderRadius: 1 }} />)}</Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell>Severity</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Root Cause</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Affected Lots</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No corrective actions recorded.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  actions.map((ca) => (
                    <TableRow key={ca.id} hover>
                      <TableCell>
                        <Chip
                          label={ca.severity}
                          size="small"
                          color={ca.severity === 'critical' ? 'error' : ca.severity === 'high' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ca.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ca.rootCause}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={ca.status.replace('_', ' ')} size="small" color={caStatusColor[ca.status] || 'default'} />
                      </TableCell>
                      <TableCell>
                        {ca.dueDate ? new Date(ca.dueDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        {ca.affectedLots?.length ? ca.affectedLots.join(', ') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {ca.status === 'open' && (
                          <Tooltip title="Start">
                            <IconButton size="small" color="primary" onClick={() => handleCaAction(ca.id, 'start')}>
                              <PlayArrow />
                            </IconButton>
                          </Tooltip>
                        )}
                        {ca.status === 'in_progress' && (
                          <Tooltip title="Complete">
                            <IconButton size="small" color="success" onClick={() => handleCaAction(ca.id, 'complete')}>
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                        {ca.status === 'completed' && (
                          <Tooltip title="Verify">
                            <IconButton size="small" color="info" onClick={() => handleCaAction(ca.id, 'verify')}>
                              <Verified />
                            </IconButton>
                          </Tooltip>
                        )}
                        {ca.status === 'verified' && (
                          <Tooltip title="Close">
                            <IconButton size="small" onClick={() => handleCaAction(ca.id, 'close')}>
                              <Close />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Monitoring Records Tab */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Monitoring Records</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddMonitoringOpen(true)}>
            Add Monitoring Record
          </Button>
        </Box>
        {monitoringLoading && monitoringRecords.length === 0 ? (
          <Box>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1, borderRadius: 1 }} />)}</Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell>Control Point ID</TableCell>
                  <TableCell>Measured Value</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Recorded By</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monitoringRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No monitoring records found. Add one to begin tracking.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  monitoringRecords.map((rec) => (
                    <TableRow key={rec.id} hover>
                      <TableCell>{rec.controlPointId}</TableCell>
                      <TableCell>{rec.measuredValue}</TableCell>
                      <TableCell>{rec.unit}</TableCell>
                      <TableCell>{rec.recordedBy}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rec.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {rec.status ? (
                          <Chip
                            label={rec.status}
                            size="small"
                            color={rec.status === 'normal' ? 'success' : rec.status === 'deviation' ? 'error' : 'default'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {rec.createdAt ? new Date(rec.createdAt).toLocaleString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Add Monitoring Record Dialog */}
      <Dialog open={addMonitoringOpen} onClose={() => setAddMonitoringOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Monitoring Record</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Control Point</InputLabel>
              <Select
                label="Control Point"
                value={monitoringForm.controlPointId}
                onChange={(e) => setMonitoringForm({ ...monitoringForm, controlPointId: e.target.value })}
              >
                {ccps.map((ccp) => (
                  <MenuItem key={ccp.id} value={ccp.id}>
                    {ccp.ccpNumber} — {ccp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Measured Value"
                  fullWidth
                  required
                  value={monitoringForm.measuredValue}
                  onChange={(e) => setMonitoringForm({ ...monitoringForm, measuredValue: e.target.value })}
                  placeholder="e.g., 74.5"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Unit"
                  fullWidth
                  value={monitoringForm.unit}
                  onChange={(e) => setMonitoringForm({ ...monitoringForm, unit: e.target.value })}
                  placeholder="e.g., °C"
                />
              </Grid>
            </Grid>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={monitoringForm.notes}
              onChange={(e) => setMonitoringForm({ ...monitoringForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMonitoringOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitMonitoringRecord}
            disabled={monitoringSubmitting || !monitoringForm.controlPointId || !monitoringForm.measuredValue}
          >
            {monitoringSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for monitoring record feedback */}
      <Snackbar
        open={monitoringSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setMonitoringSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setMonitoringSnackbar(prev => ({ ...prev, open: false }))}
          severity={monitoringSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {monitoringSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Create CCP Dialog */}
      <Dialog open={createCcpOpen} onClose={() => setCreateCcpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create HACCP Control Point</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField label="CCP Number" fullWidth value={newCcp.ccpNumber}
                  onChange={(e) => setNewCcp({ ...newCcp, ccpNumber: e.target.value })} placeholder="e.g., CCP-01" />
              </Grid>
              <Grid item xs={8}>
                <TextField label="Name" fullWidth value={newCcp.name}
                  onChange={(e) => setNewCcp({ ...newCcp, name: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Control Point Type</InputLabel>
                  <Select label="Control Point Type" value={newCcp.controlPointType}
                    onChange={(e) => setNewCcp({ ...newCcp, controlPointType: e.target.value })}>
                    <MenuItem value="ccp">CCP - Critical Control Point</MenuItem>
                    <MenuItem value="oprp">OPRP - Operational Prerequisite</MenuItem>
                    <MenuItem value="prp">PRP - Prerequisite Program</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Hazard Type</InputLabel>
                  <Select label="Hazard Type" value={newCcp.hazardType}
                    onChange={(e) => setNewCcp({ ...newCcp, hazardType: e.target.value })}>
                    <MenuItem value="biological">Biological</MenuItem>
                    <MenuItem value="chemical">Chemical</MenuItem>
                    <MenuItem value="physical">Physical</MenuItem>
                    <MenuItem value="allergen">Allergen</MenuItem>
                    <MenuItem value="radiological">Radiological</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Hazard Description" fullWidth multiline rows={2} value={newCcp.hazardDescription}
                  onChange={(e) => setNewCcp({ ...newCcp, hazardDescription: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Control Measure" fullWidth value={newCcp.controlMeasure}
                  onChange={(e) => setNewCcp({ ...newCcp, controlMeasure: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Critical Limit" fullWidth value={newCcp.criticalLimit}
                  onChange={(e) => setNewCcp({ ...newCcp, criticalLimit: e.target.value })} placeholder="e.g., >= 72C for 15s" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Monitoring Procedure" fullWidth value={newCcp.monitoringProcedure}
                  onChange={(e) => setNewCcp({ ...newCcp, monitoringProcedure: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Monitoring Frequency" fullWidth value={newCcp.monitoringFrequency}
                  onChange={(e) => setNewCcp({ ...newCcp, monitoringFrequency: e.target.value })} placeholder="e.g., Every batch" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Corrective Action Plan" fullWidth multiline rows={2} value={newCcp.correctiveActionPlan}
                  onChange={(e) => setNewCcp({ ...newCcp, correctiveActionPlan: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Verification Procedure" fullWidth value={newCcp.verificationProcedure}
                  onChange={(e) => setNewCcp({ ...newCcp, verificationProcedure: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Verification Frequency" fullWidth value={newCcp.verificationFrequency}
                  onChange={(e) => setNewCcp({ ...newCcp, verificationFrequency: e.target.value })} placeholder="e.g., Weekly" />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCcpOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCcp}
            disabled={submitting || !newCcp.ccpNumber || !newCcp.name || !newCcp.hazardDescription}>
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
